import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import GeoContext from 'contexts/geoContext'
import CurrencyContext from 'contexts/currencyContext'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import FilterChip from 'LuxKit/Chips/FilterChip'
import LineMapIcon from 'LuxKit/Icons/line/LineMapIcon'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import Places from 'constants/places'
import { buildUrl } from 'lib/url/searchUrlUtils'
import { replaceExperienceQueryString } from 'lib/experiences/experienceUtils'
import TripDrawer from 'tripPlanner/components/GenericDrawer/TripDrawer'
import useExperienceSearchUrl from 'hooks/Experiences/useExperienceSearchUrl'
import { usePlaceByName } from 'hooks/usePlace'
import BrowseAllResultsFooter from '../BrowseAllResultsFooter'
import AddToTripAction from 'tripPlanner/components/GenericDrawer/AddToTripAction'
import ExperienceDetailView from './ExperienceDetailView'
import ExperienceListingDrawerHeader from './ExperienceListingDrawerHeader'
import ExperienceSearchResults from './ExperienceSearchResults'
import type { DrawerExperienceSearchTile } from './ExperienceDetailView'
import { mapExperienceDetailToDrawer } from './mapExperienceDetailToDrawer'
import useExperienceDetail from '../../hooks/useExperienceDetail'
import useOfferReviews from '../../hooks/useOfferReviews'
import useExperienceSearch from './useExperienceSearch'
import type { TripItineraryData } from 'tripPlanner/api/types'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import { useExperiences } from 'tripPlanner/hooks/api/offer'
import uuidV4 from 'lib/string/uuidV4Utils'
import useTripPlannerOfferListTracking from '../../hooks/useTripPlannerOfferListTracking'
import type { SendMessageOptions } from 'tripPlanner/components/AddToTrip/useAddHotelToTrip'

const ContentWrapper = styled.div`
  margin: 0 auto;
  padding: ${rem(24)};
  width: ${rem(700)};
  max-width: 100%;
  box-sizing: border-box;
`

const FullWidthLoadingBox = styled(LoadingBox)`
  width: 100%;
`

function isConcreteExperienceSearchUrl(searchUrl: string) {
  const searchParams = new URLSearchParams(searchUrl.split('?')[1] ?? '')
  const destinationId = searchParams.get('destinationId')

  return !!destinationId && destinationId !== Places.Anywhere.id
}

export interface ExperienceListingDrawerProps {
  isOpen: boolean
  onClose: () => void
  placeName: string
  placeCountry: string
  stopCoordinates?: { lat: number; lng: number }
  onExploreMap?: () => void
  itineraryData?: TripItineraryData
  onSendMessage?: (message: string, options?: SendMessageOptions) => void
  onShowExperienceOnMap?: (experience: { offerId: string; name: string; geoLocation: { lat: number; lng: number } }) => void
}

function ExperienceListingDrawer({
  isOpen,
  onClose,
  placeName,
  placeCountry,
  stopCoordinates,
  onExploreMap,
  itineraryData,
  onSendMessage,
  onShowExperienceOnMap,
}: ExperienceListingDrawerProps) {
  const intl = useIntl()
  const geo = useContext(GeoContext)
  const currency = useContext(CurrencyContext)
  const [selectedOfferId, setSelectedOfferId] = useState<string | undefined>(undefined)
  const [drawerSessionKey, setDrawerSessionKey] = useState(() => uuidV4())
  const wasOpenRef = useRef(false)
  const searchQuery = `${placeName}, ${placeCountry}`

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setDrawerSessionKey(uuidV4())
    }

    wasOpenRef.current = isOpen
  }, [isOpen])
  const [resolvedExperienceSearchUrl] = useExperienceSearchUrl({
    latLong: {
      latitude: stopCoordinates?.lat,
      longitude: stopCoordinates?.lng,
    },
    placeLevels: ['multi_city_vicinity', 'province_state', 'city'],
  })
  const resolvedPlaceByName = usePlaceByName(placeName)

  const { experiences, isLoading } = useExperienceSearch({
    searchQuery,
    region: geo.currentRegionCode,
    enabled: isOpen,
  })
  const experienceOfferIds = useMemo(() => experiences.map((experience) => experience.offerId), [experiences])
  const trackingOffers = useExperiences({
    currentCurrency: currency,
    experienceIds: isOpen ? experienceOfferIds : [],
    currentRegionCode: geo.currentRegionCode,
  })
  const trackingOfferById = useMemo(() => {
    return new Map(trackingOffers.data.map((offer) => [offer.id, offer]))
  }, [trackingOffers.data])
  const getTrackingOffer = useCallback((offerId: string) => {
    return trackingOfferById.get(offerId)
  }, [trackingOfferById])

  const { experienceDetail, isLoading: isDetailLoading, isError: isDetailError, refetch: refetchDetail } = useExperienceDetail({
    offerId: selectedOfferId,
    currencyCode: currency,
    enabled: !!selectedOfferId,
  })

  const { reviews, isLoading: reviewsLoading } = useOfferReviews({
    offerId: selectedOfferId,
    region: geo.currentRegionCode,
    enabled: !!selectedOfferId && !!experienceDetail,
  })

  const detailExperience = useMemo(() => {
    if (!experienceDetail) return undefined
    return mapExperienceDetailToDrawer(experienceDetail)
  }, [experienceDetail])

  const handleSelectExperience = useCallback((experience: DrawerExperienceSearchTile) => {
    setSelectedOfferId(experience.offerId)
  }, [])

  const handleBack = useCallback(() => {
    setSelectedOfferId(undefined)
  }, [])

  const handleClose = useCallback(() => {
    setSelectedOfferId(undefined)
    onClose()
  }, [onClose])

  const handleAfterClose = useCallback(() => {
    setSelectedOfferId(undefined)
  }, [])

  const handleRetryDetail = useCallback(() => {
    refetchDetail()
  }, [refetchDetail])

  const isDetailView = !!selectedOfferId
  const hasExperiences = experiences.length > 0
  const browseAllResultsUrl = useMemo(() => {
    if (resolvedPlaceByName?.id && resolvedPlaceByName.id !== Places.Anywhere.id) {
      return buildUrl(
        `/search/experiences?${replaceExperienceQueryString({
          destinationId: resolvedPlaceByName.id,
          destinationName: resolvedPlaceByName.name,
        })}`,
        geo.currentRegionCode,
      )
    }

    if (isConcreteExperienceSearchUrl(resolvedExperienceSearchUrl)) {
      return buildUrl(resolvedExperienceSearchUrl, geo.currentRegionCode)
    }

    return buildUrl('/experiences', geo.currentRegionCode)
  }, [geo.currentRegionCode, resolvedExperienceSearchUrl, resolvedPlaceByName])

  const handleShowMapFromHeader = useCallback(() => {
    if (detailExperience?.geoLocation) {
      onShowExperienceOnMap?.({
        offerId: detailExperience.offerId,
        name: detailExperience.name,
        geoLocation: detailExperience.geoLocation,
      })
    }
  }, [detailExperience, onShowExperienceOnMap])

  const headerActions = useMemo(() => {
    if (!isDetailView || !detailExperience) return undefined

    const showMapLabel = intl.formatMessage({
      id: '5HOM8ZapOXbEwqgm-QaI',
      defaultMessage: 'Show map',
      description: 'Button label shown in the experience drawer header to open the map for the current location or offer.',
    })

    return (
      <Group direction="horizontal" gap={8} verticalAlign="center">
        {detailExperience.geoLocation && (
          <FilterChip size="small" startIcon={<LineMapIcon />} onClick={handleShowMapFromHeader}>
            {showMapLabel}
          </FilterChip>
        )}
        {itineraryData && onSendMessage && (
          <AddToTripAction
            itineraryData={itineraryData}
            onSendMessage={onSendMessage}
            placeName={detailExperience.name}
            offerId={detailExperience.offerId}
            offerType="experience"
            coordinates={detailExperience.geoLocation}
            variant="outline"
            onAfterAction={handleClose}
          />
        )}
      </Group>
    )
  }, [detailExperience, handleClose, handleShowMapFromHeader, intl, isDetailView, itineraryData, onSendMessage])

  const tracking = useTripPlannerOfferListTracking(
    'experienceSearchDrawer',
    `${drawerSessionKey}:${searchQuery}`,
  )

  const browseAllResultsFooter = useMemo(() => {
    if (isDetailView || !hasExperiences) return undefined

    return (
      <BrowseAllResultsFooter
        heading={intl.formatMessage({
          id: 'oDfappyrw_ua6zZW7yVX',
          defaultMessage: 'There’s plenty more to explore',
          description: 'Prompt heading inviting users to browse all experience search results',
        })}
        body={intl.formatMessage({
          id: '8ws0EWfQYDiEghnOFFCP',
          defaultMessage: 'Browse all available experiences for this destination.',
          description: 'Supporting text encouraging users to view more experience search results',
        })}
        browseAllResultsUrl={browseAllResultsUrl}
        buttonLabel={intl.formatMessage({
          id: 'nemeMlkPWyHbg8VU1XwY',
          defaultMessage: 'Browse all results',
          description: 'Button label linking to the full experience search results page',
        })}
      />
    )
  }, [browseAllResultsUrl, hasExperiences, intl, isDetailView])

  return (
    <TripDrawer
      isOpen={isOpen}
      onClose={handleClose}
      onAfterClose={handleAfterClose}
      onBack={isDetailView ? handleBack : undefined}
      direction="right-to-left"
      footer={browseAllResultsFooter}
      headerActions={headerActions}
    >
      {isDetailView && (
        <>
          {isDetailLoading && !detailExperience && (
            <ContentWrapper>
              <Group direction="vertical" gap={16}>
                <FullWidthLoadingBox height={200} borderRadius="M" />
                <LoadingBox height={28} width={240} borderRadius="S" />
                <LoadingBox height={20} width={160} borderRadius="S" />
              </Group>
            </ContentWrapper>
          )}
          {isDetailError && !isDetailLoading && !detailExperience && (
            <ContentWrapper>
              <Group direction="vertical" gap={8} horizontalAlign="center" verticalAlign="center">
                <BodyText variant="medium" colour="critical">
                  {intl.formatMessage({
                    id: 'clAQJsTYp1RaYNVY1-Rz',
                    defaultMessage: 'Failed to load experience details.',
                    description: 'Error message shown when experience offer details fail to load in the drawer',
                  })}
                </BodyText>
                <TextButton kind="secondary" size="small" onClick={handleRetryDetail}>
                  {intl.formatMessage({
                    id: 'q2nIV5-Sco41UZtxNQNg',
                    defaultMessage: 'Try again',
                    description: 'Button label to retry loading hotel offer details after an error',
                  })}
                </TextButton>
              </Group>
            </ContentWrapper>
          )}
          {detailExperience && (
            <ExperienceDetailView
              key={detailExperience.offerId}
              experience={detailExperience}
              standaloneOffer={trackingOfferById.get(detailExperience.offerId)}
              reviews={reviews}
              reviewsLoading={reviewsLoading}
              itineraryData={itineraryData}
              onSendMessage={onSendMessage}
              onAfterAddToTrip={handleClose}
              scrollToPricing={false}
            />
          )}
        </>
      )}

      {!isDetailView && (
        <ContentWrapper>
          <Group direction="vertical" gap={24}>
            <ExperienceListingDrawerHeader
              placeName={placeName}
              onExploreMap={onExploreMap}
            />

            <OfferListEventsProvider tracking={tracking}>
              <ExperienceSearchResults
                experiences={experiences}
                isLoading={isLoading}
                onSelectExperience={handleSelectExperience}
                placeName={placeName}
                browseAllResultsUrl={browseAllResultsUrl}
                itineraryData={itineraryData}
                onSendMessage={onSendMessage}
                getTrackingOffer={getTrackingOffer}
              />
            </OfferListEventsProvider>
          </Group>
        </ContentWrapper>
      )}
    </TripDrawer>
  )
}

export default ExperienceListingDrawer
